// ==========================================================
// components/LegalDocumentScreen.tsx
// Composant partagé pour tous les écrans légaux Harmonia
// ==========================================================
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Les props (title, date, sections, stats, badgeText) restent fournies
// déjà traduites par l'écran appelant (cgu.tsx, confidentialite.tsx,
// politique-cookies.tsx, regles-communautaires.tsx) — seul le texte en
// dur propre à ce composant partagé (intro, pills, séparateurs, carte
// contact, footer légal) passe par t(). Icônes, couleurs et routes
// restent codées en dur, inchangées.

import React, { useRef, useEffect, useState } from 'react';
import {
  StyleSheet, Text, View, ScrollView,
  TouchableOpacity, Platform, Animated,
  UIManager, LayoutAnimation,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import HarmoniaLogo from './HarmoniaLogo';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

// ─── Palette ──────────────────────────────────────────────────────────────────
const VIOLET       = '#6411A9';
const VIOLET_DARK  = '#4A0D7C';
const VIOLET_DEEP  = '#2D0850';
const VIOLET_LIGHT = '#9B30E8';
const VIOLET_TINT  = '#F3EAFF';
const VIOLET_TINT2 = '#EAD9FF';
const GOLD         = '#F5A623';
const GOLD_SOFT    = '#FFC947';
const LIGHT_BG     = '#F7F3FF';
const WHITE        = '#FFFFFF';
const CARD_BORDER  = '#E8DEFF';
const CARD_BORDER2 = '#D4C4F0';
const TEXT_MAIN    = '#1A0A2E';
const TEXT_SUB     = '#5C4A7A';
const TEXT_MUTED   = '#9B8DB5';
const SUCCESS      = '#00A86B';
const DANGER       = '#E8003D';

// ─── Types ────────────────────────────────────────────────────────────────────
export type ContentItem =
  | { type: 'paragraph'; text: string }
  | { type: 'bullet';    items: string[] }
  | { type: 'subtitle';  text: string }
  | { type: 'highlight'; text: string; icon?: string };

export interface LegalSection {
  id:      string;
  num:     string;
  icon:    string;
  color:   string;
  title:   string;
  content: ContentItem[];
}

export interface StatItem {
  value?:      string;
  label:       string;
  isIcon?:     boolean;
  iconName?:   string;
  iconColor?:  string;
}

export interface LegalDocProps {
  title:        string;
  version:      string;
  date?:        string;
  sections:     LegalSection[];
  currentRoute: string;
  stats:        StatItem[];
  badgeText?:   string;
}

// ─── AnimCard ─────────────────────────────────────────────────────────────────
function AnimCard({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const anim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(anim, { toValue: 1, duration: 520, delay, useNativeDriver: true }).start();
  }, []);
  return (
    <Animated.View style={{
      opacity: anim,
      transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [24, 0] }) }],
    }}>
      {children}
    </Animated.View>
  );
}

// ─── ContentRenderer ──────────────────────────────────────────────────────────
function ContentRenderer({ items }: { items: ContentItem[] }) {
  return (
    <View style={{ gap: 10 }}>
      {items.map((item, i) => {
        if (item.type === 'paragraph') {
          return <Text key={i} style={styles.contentParagraph}>{item.text}</Text>;
        }
        if (item.type === 'subtitle') {
          return (
            <View key={i} style={styles.subtitleRow}>
              <View style={styles.subtitleLine} />
              <Text style={styles.contentSubtitle}>{item.text}</Text>
            </View>
          );
        }
        if (item.type === 'highlight') {
          return (
            <View key={i} style={styles.highlightBox}>
              <Ionicons name={(item.icon ?? 'information-circle') as any} size={16} color={VIOLET} />
              <Text style={styles.highlightText}>{item.text}</Text>
            </View>
          );
        }
        if (item.type === 'bullet') {
          return (
            <View key={i} style={{ gap: 7 }}>
              {item.items.map((bullet, j) => (
                <View key={j} style={styles.bulletRow}>
                  <View style={styles.bulletDot} />
                  <Text style={styles.bulletText}>{bullet}</Text>
                </View>
              ))}
            </View>
          );
        }
        return null;
      })}
    </View>
  );
}

// ─── AccordionSection ─────────────────────────────────────────────────────────
function AccordionSection({ section, delay }: { section: LegalSection; delay: number }) {
  const [open, setOpen] = useState(false);
  const rotateAnim = useRef(new Animated.Value(0)).current;

  const toggle = () => {
    const next = !open;
    LayoutAnimation.configureNext({
      duration: 280,
      create:  { type: 'easeInEaseOut', property: 'opacity' },
      update:  { type: 'easeInEaseOut' },
      delete:  { type: 'easeInEaseOut', property: 'opacity' },
    });
    setOpen(next);
    Animated.timing(rotateAnim, { toValue: next ? 1 : 0, duration: 260, useNativeDriver: true }).start();
  };

  const rotate = rotateAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] });

  return (
    <AnimCard delay={delay}>
      <View style={[styles.accordionCard, open && styles.accordionCardOpen]}>
        <TouchableOpacity style={styles.accordionHeader} onPress={toggle} activeOpacity={0.72}>
          <View style={[styles.numBadge, { borderColor: section.color + '40', backgroundColor: section.color + '12' }]}>
            <Text style={[styles.numText, { color: section.color }]}>{section.num}</Text>
          </View>
          <View style={[styles.iconBox, { backgroundColor: section.color + '15' }]}>
            <Ionicons name={section.icon as any} size={19} color={section.color} />
          </View>
          <Text style={styles.accordionTitle} numberOfLines={1}>{section.title}</Text>
          <Animated.View style={[styles.chevronBox, { transform: [{ rotate }] }]}>
            <Ionicons name="chevron-down" size={16} color={TEXT_MUTED} />
          </Animated.View>
        </TouchableOpacity>
        {open && (
          <View style={styles.accordionContent}>
            <View style={[styles.contentSeparator, { backgroundColor: section.color + '25' }]} />
            <ContentRenderer items={section.content} />
          </View>
        )}
      </View>
    </AnimCard>
  );
}

// ─── LegalFooter ──────────────────────────────────────────────────────────────
function LegalFooter({ currentRoute }: { currentRoute: string }) {
  const { t } = useTranslation();
  const router = useRouter();

  // Libellés traduits, routes toujours codées en dur
  const LEGAL_LINKS = [
    { label: t('legalDocumentScreen.footer.privacy'),  route: '/confidentialite' },
    { label: t('legalDocumentScreen.footer.cookies'),  route: '/politique-cookies' },
    { label: t('legalDocumentScreen.footer.terms'),    route: '/cgu' },
    { label: t('legalDocumentScreen.footer.rules'),    route: '/regles-communautaires' },
  ];

  return (
    <View style={styles.legalFooter}>
      {LEGAL_LINKS.map((link, i) => (
        <React.Fragment key={link.route}>
          {i > 0 && <Text style={styles.footerDot}>·</Text>}
          <TouchableOpacity
            onPress={() => currentRoute !== link.route && router.push(link.route as any)}
            activeOpacity={0.65}
          >
            <Text style={[styles.footerLink, currentRoute === link.route && styles.footerLinkActive]}>
              {link.label}
            </Text>
          </TouchableOpacity>
        </React.Fragment>
      ))}
    </View>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LegalDocumentScreen({
  title,
  version,
  date = '[DATE]',
  sections,
  currentRoute,
  stats,
  badgeText,
}: LegalDocProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const resolvedBadgeText = badgeText ?? t('legalDocumentScreen.defaultBadgeText');

  return (
    <View style={styles.root}>
      <View style={styles.orb1} />
      <View style={styles.orb2} />

      {/* ── Header ── */}
      <LinearGradient
        colors={[VIOLET_DEEP, VIOLET_DARK, VIOLET]}
        start={{ x: 0.1, y: 0 }} end={{ x: 0.9, y: 1 }}
        style={styles.header}
      >
        <TouchableOpacity style={styles.backBtn} onPress={() => router.back()} activeOpacity={0.75}>
          <Ionicons name="arrow-back" size={20} color="#fff" />
        </TouchableOpacity>

        <HarmoniaLogo width={60} height={60} />

        <View style={styles.legalBadge}>
          <Ionicons name="shield-checkmark" size={11} color={GOLD} />
          <Text style={styles.legalBadgeText}>{resolvedBadgeText} · {version}</Text>
        </View>

        <Text style={styles.headerTitle}>{title}</Text>
        <Text style={styles.headerSub}>{t('legalDocumentScreen.lastUpdated', { date })}</Text>

        <View style={styles.statsRow}>
          {stats.map((stat, i) => (
            <React.Fragment key={i}>
              {i > 0 && <View style={styles.statDiv} />}
              <View style={styles.statItem}>
                {stat.isIcon
                  ? <Ionicons name={stat.iconName as any} size={16} color={stat.iconColor ?? GOLD_SOFT} />
                  : <Text style={styles.statNum}>{stat.value}</Text>
                }
                <Text style={styles.statLabel}>{stat.label}</Text>
              </View>
            </React.Fragment>
          ))}
        </View>
      </LinearGradient>

      {/* ── ScrollView ── */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
      >
        <AnimCard delay={0}>
          <View style={styles.introBanner}>
            <View style={styles.introIconBox}>
              <Ionicons name="shield-half-outline" size={26} color={VIOLET} />
            </View>
            <View style={{ flex: 1, gap: 4 }}>
              <Text style={styles.introTitle}>{t('legalDocumentScreen.intro.title')}</Text>
              <Text style={styles.introText}>
                {t('legalDocumentScreen.intro.text')}
              </Text>
            </View>
          </View>
        </AnimCard>

        <AnimCard delay={80}>
          <View style={styles.pillsRow}>
            {[
              { icon: 'ban-outline',         label: t('legalDocumentScreen.pills.zeroTracking'),  color: DANGER },
              { icon: 'lock-closed-outline', label: t('legalDocumentScreen.pills.encrypted'),      color: SUCCESS },
              { icon: 'globe-outline',       label: t('legalDocumentScreen.pills.gdpr'),           color: VIOLET },
              { icon: 'megaphone-outline',   label: t('legalDocumentScreen.pills.internalAdsOnly'),color: GOLD },
            ].map((p) => (
              <View key={p.label} style={[styles.pill, { borderColor: p.color + '40', backgroundColor: p.color + '0E' }]}>
                <Ionicons name={p.icon as any} size={12} color={p.color} />
                <Text style={[styles.pillText, { color: p.color }]}>{p.label}</Text>
              </View>
            ))}
          </View>
        </AnimCard>

        <AnimCard delay={120}>
          <View style={styles.sectionDivider}>
            <View style={styles.sectionDividerLine} />
            <Text style={styles.sectionDividerText}>{t('legalDocumentScreen.sectionsLabel')}</Text>
            <View style={styles.sectionDividerLine} />
          </View>
        </AnimCard>

        {sections.map((section, i) => (
          <AccordionSection key={section.id} section={section} delay={160 + i * 35} />
        ))}

        <AnimCard delay={sections.length * 35 + 200}>
          <View style={styles.contactCard}>
            <View style={styles.contactIconBox}>
              <Ionicons name="mail" size={22} color={WHITE} />
            </View>
            <View style={{ flex: 1, gap: 3 }}>
              <Text style={styles.contactTitle}>{t('legalDocumentScreen.contact.title')}</Text>
              <Text style={styles.contactEmail}>harmonia@harmoniaworld.world</Text>
            </View>
          </View>
        </AnimCard>

        <AnimCard delay={sections.length * 35 + 260}>
          <View style={styles.docFooter}>
            <HarmoniaLogo width={18} height={18} />
            <Text style={styles.docFooterText}>{t('legalDocumentScreen.copyright', { year: 2026 })}</Text>
          </View>
        </AnimCard>

        <View style={{ height: 8 }} />
      </ScrollView>

      {/* ── Footer légal fixe ── */}
      <LegalFooter currentRoute={currentRoute} />
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: LIGHT_BG },
  orb1: { position: 'absolute', top: 220, right: -60, width: 200, height: 200, borderRadius: 100, backgroundColor: VIOLET + '09' },
  orb2: { position: 'absolute', bottom: 80, left: -70, width: 170, height: 170, borderRadius: 85, backgroundColor: GOLD + '08' },

  // Header
  header: { paddingTop: Platform.OS === 'ios' ? 58 : 42, paddingBottom: 22, paddingHorizontal: 20, alignItems: 'center', gap: 9 },
  backBtn: { position: 'absolute', top: Platform.OS === 'ios' ? 56 : 40, left: 18, width: 38, height: 38, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.15)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.22)', justifyContent: 'center', alignItems: 'center' },
  legalBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: GOLD + '22', borderWidth: 1, borderColor: GOLD + '50', borderRadius: 20, paddingHorizontal: 10, paddingVertical: 4 },
  legalBadgeText: { fontSize: 10, color: GOLD_SOFT, fontWeight: '700', letterSpacing: 0.4 },
  headerTitle: { fontSize: 17, fontWeight: '800', color: '#fff', textAlign: 'center', letterSpacing: 0.2 },
  headerSub: { fontSize: 11, color: 'rgba(255,255,255,0.55)' },
  statsRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.10)', borderRadius: 14, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', paddingVertical: 11, paddingHorizontal: 18, gap: 14, marginTop: 2 },
  statItem: { alignItems: 'center', gap: 3 },
  statNum: { fontSize: 17, fontWeight: '800', color: '#fff' },
  statLabel: { fontSize: 9, color: 'rgba(255,255,255,0.6)', fontWeight: '500', textAlign: 'center' },
  statDiv: { width: 1, height: 26, backgroundColor: 'rgba(255,255,255,0.15)' },

  // Scroll
  scroll: { paddingHorizontal: 15, paddingTop: 16, paddingBottom: 14, gap: 10 },

  // Intro
  introBanner: { flexDirection: 'row', alignItems: 'flex-start', gap: 13, backgroundColor: WHITE, borderRadius: 16, padding: 16, borderWidth: 1, borderColor: CARD_BORDER, shadowColor: VIOLET, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 8, elevation: 2 },
  introIconBox: { width: 44, height: 44, borderRadius: 13, backgroundColor: VIOLET_TINT, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  introTitle: { fontSize: 14, fontWeight: '800', color: TEXT_MAIN },
  introText: { fontSize: 12, color: TEXT_SUB, lineHeight: 18 },

  // Pills
  pillsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  pill: { flexDirection: 'row', alignItems: 'center', gap: 5, borderWidth: 1, borderRadius: 20, paddingHorizontal: 10, paddingVertical: 5 },
  pillText: { fontSize: 10, fontWeight: '700', letterSpacing: 0.3 },

  // Section divider
  sectionDivider: { flexDirection: 'row', alignItems: 'center', gap: 10, marginVertical: 2 },
  sectionDividerLine: { flex: 1, height: 1, backgroundColor: CARD_BORDER2 },
  sectionDividerText: { fontSize: 10, color: TEXT_MUTED, fontWeight: '700', letterSpacing: 1.5 },

  // Accordion
  accordionCard: { backgroundColor: WHITE, borderRadius: 15, borderWidth: 1, borderColor: CARD_BORDER, overflow: 'hidden', shadowColor: VIOLET, shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 6, elevation: 1 },
  accordionCardOpen: { borderColor: VIOLET + '60', shadowOpacity: 0.12, shadowRadius: 10, elevation: 3 },
  accordionHeader: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, paddingHorizontal: 15, gap: 11 },
  numBadge: { width: 30, height: 30, borderRadius: 9, borderWidth: 1, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  numText: { fontSize: 10, fontWeight: '900', letterSpacing: 0.4 },
  iconBox: { width: 36, height: 36, borderRadius: 11, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  accordionTitle: { flex: 1, fontSize: 13, fontWeight: '700', color: TEXT_MAIN },
  chevronBox: { width: 26, height: 26, borderRadius: 8, backgroundColor: VIOLET_TINT, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  accordionContent: { paddingHorizontal: 15, paddingBottom: 16, gap: 10 },
  contentSeparator: { height: 1, marginBottom: 6 },

  // Content types
  contentParagraph: { fontSize: 13, color: TEXT_SUB, lineHeight: 20 },
  subtitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
  subtitleLine: { width: 3, height: 14, borderRadius: 2, backgroundColor: VIOLET },
  contentSubtitle: { fontSize: 12, fontWeight: '800', color: VIOLET },
  highlightBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 9, backgroundColor: VIOLET_TINT, borderLeftWidth: 3, borderLeftColor: VIOLET, borderRadius: 10, padding: 12 },
  highlightText: { flex: 1, fontSize: 13, color: TEXT_MAIN, fontWeight: '600', lineHeight: 19 },
  bulletRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  bulletDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: VIOLET, marginTop: 7, flexShrink: 0 },
  bulletText: { flex: 1, fontSize: 13, color: TEXT_SUB, lineHeight: 19 },

  // Contact card
  contactCard: { flexDirection: 'row', alignItems: 'center', gap: 14, backgroundColor: WHITE, borderRadius: 16, padding: 18, borderWidth: 1, borderColor: VIOLET + '30', shadowColor: VIOLET, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.10, shadowRadius: 10, elevation: 3 },
  contactIconBox: { width: 48, height: 48, borderRadius: 14, backgroundColor: VIOLET, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  contactTitle: { fontSize: 13, fontWeight: '700', color: TEXT_MAIN },
  contactEmail: { fontSize: 12, color: VIOLET, fontWeight: '600', marginTop: 2 },

  // Doc footer
  docFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 8 },
  docFooterText: { fontSize: 11, color: TEXT_MUTED },

  // ── Legal footer fixe ──
  legalFooter: {
    flexDirection:     'row',
    alignItems:        'center',
    justifyContent:    'center',
    flexWrap:          'wrap',
    gap:               2,
    paddingVertical:   9,
    paddingHorizontal: 12,
    backgroundColor:   WHITE,
    borderTopWidth:    1,
    borderTopColor:    CARD_BORDER,
  },
  footerLink: {
    fontSize:          10,
    color:             TEXT_MUTED,
    fontWeight:        '500',
    paddingHorizontal: 4,
    paddingVertical:   3,
  },
  footerLinkActive: {
    color:      VIOLET,
    fontWeight: '800',
  },
  footerDot: { fontSize: 10, color: CARD_BORDER2 },
});
